import { useState, useCallback } from "react";
import type { OnChangeFn, VisibilityState } from "@tanstack/react-table";

interface UseColumnVisibilityOptions {
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  onColumnHide?: (columnId: string) => void;
}

export const useColumnVisibility = ({
  columnVisibility: controlledVisibility,
  onColumnVisibilityChange: controlledOnChange,
  onColumnHide,
}: UseColumnVisibilityOptions) => {
  const isControlled = controlledVisibility !== undefined;

  const [internalVisibility, setInternalVisibility] = useState<VisibilityState>(
    {}
  );

  const visibility = isControlled ? controlledVisibility : internalVisibility;

  const onColumnVisibilityChange: OnChangeFn<VisibilityState> = useCallback(
    updaterOrValue => {
      const newVisibility =
        typeof updaterOrValue === "function"
          ? updaterOrValue(visibility)
          : updaterOrValue;

      if (!isControlled) {
        setInternalVisibility(newVisibility);
      }

      controlledOnChange?.(updaterOrValue);
    },
    [visibility, isControlled, controlledOnChange]
  );

  const hideColumn = useCallback(
    (columnId: string) => {
      if (onColumnHide) {
        onColumnHide(columnId);
      } else {
        onColumnVisibilityChange(old => ({ ...old, [columnId]: false }));
      }
    },
    [onColumnVisibilityChange, onColumnHide]
  );

  const showColumn = useCallback(
    (columnId: string) => {
      onColumnVisibilityChange(old => ({ ...old, [columnId]: true }));
    },
    [onColumnVisibilityChange]
  );

  const toggleColumnVisibility = useCallback(
    (columnId: string) => {
      const isVisible = visibility[columnId] !== false;

      if (isVisible) {
        hideColumn(columnId);
      } else {
        showColumn(columnId);
      }
    },
    [visibility, hideColumn, showColumn]
  );

  return {
    columnVisibility: visibility,
    onColumnVisibilityChange,
    hideColumn,
    showColumn,
    toggleColumnVisibility,
  };
};
